// REST client for the v3 API.
//
// Token policy (00 §4.5, recon-verified): the backend DOES support web cookie
// refresh — for X-Client-Id: c-ls-home-web, login strips refresh_token from the body
// and sets an httpOnly Secure SameSite=Strict cookie scoped to /v3/auth/refresh.
// Therefore: access_token lives in JS MEMORY ONLY (never localStorage/sessionStorage);
// refresh rides the httpOnly cookie via credentials:'include' on /v3/auth/refresh
// ONLY (every other request goes out without credentials).
//
// Envelope: success {data, request_id} / error {error:{code,message}}.

import { isEdgeAi } from './eventAi';
import { rememberDeviceTz } from './camTz';
import { config } from '../config';
import i18n from '../i18n';
import { recordApiExchange } from './diagnostics';
import { detectDeviceLabel } from './deviceLabel';

// ---- in-memory token store ----

type TokenListener = (token: string | null) => void;

let accessToken: string | null = null;
// Absolute expiry (epoch seconds) of the in-memory access token, derived from the
// login/refresh response `expires_in`. Null when unknown (no token, or a caller set
// a token without an expiry). Drives the spec 10 §4.3 reconnect freshness check:
// "每次重連前檢查 token (now > exp-60s 才先 refresh)" — so a still-fresh token
// reconnects WITHOUT a needless /v3/auth/refresh round-trip (and one fewer chance to
// be mis-killed by a transient refresh failure).
let accessTokenExp: number | null = null;
const tokenListeners = new Set<TokenListener>();

/** Skew (s) before the real exp at which the token is treated as needing refresh. */
const TOKEN_REFRESH_SKEW_S = 60;

// Session epoch: bumped whenever the session ends (logout / hard 401). An in-flight
// refresh captures the epoch at start and refuses to write the token back if the
// epoch moved — otherwise a refresh racing a logout would resurrect the access token
// for a just-logged-out session (00 §4.5 in-memory policy must not survive logout).
let authEpoch = 0;

/**
 * End the current auth session: drop the in-memory token AND invalidate any in-flight
 * refresh (single-flight slot + epoch bump). Call this anywhere the session is being
 * terminated (logout, unrecoverable 401) instead of a bare tokenStore.clear().
 */
export function endAuthSession(): void {
  authEpoch += 1;
  refreshing = null; // a refresh in flight no longer belongs to any live session
  tokenStore.clear();
}

export const tokenStore = {
  get(): string | null {
    return accessToken;
  },
  /**
   * Set the in-memory access token. `expiresInSeconds` (the login/refresh
   * `expires_in`) records when it expires so {@link tokenStore.isFresh} can short-
   * circuit a needless pre-reconnect refresh. Omit it to clear the expiry (the token
   * is then always treated as stale → refresh-before-reconnect, the safe default).
   */
  set(token: string | null, expiresInSeconds?: number): void {
    accessToken = token;
    accessTokenExp =
      token && typeof expiresInSeconds === 'number' && expiresInSeconds > 0
        ? Math.floor(Date.now() / 1000) + expiresInSeconds
        : null;
    for (const fn of tokenListeners) fn(token);
  },
  clear(): void {
    tokenStore.set(null);
  },
  /**
   * True iff a token is held AND it is still comfortably valid (now < exp − skew).
   * Returns false when the expiry is unknown so the caller refreshes to be safe.
   */
  isFresh(): boolean {
    if (!accessToken || accessTokenExp === null) return false;
    return Math.floor(Date.now() / 1000) < accessTokenExp - TOKEN_REFRESH_SKEW_S;
  },
  subscribe(fn: TokenListener): () => void {
    tokenListeners.add(fn);
    return () => tokenListeners.delete(fn);
  },
};

// ---- errors ----

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  /** 原始 error 物件（code/message 之外的結構欄位，如 422 WEAK_PASSWORD 的 `policy`）。 */
  readonly details: Record<string, unknown> | null;

  constructor(status: number, code: string, message: string, requestId: string | null, details: Record<string, unknown> | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

// ---- unauthorized hook (auth context registers a logout handler) ----

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Register the session-expired handler (refresh failed or second 401). One handler max. */
export function onUnauthorized(fn: UnauthorizedHandler | null): void {
  unauthorizedHandler = fn;
}

// ---- forced-upgrade hook (426 UPGRADE_REQUIRED) ----
// 20 §8.2 / 00 §4.2: ANY REST response carrying 426 (X-Client-Version below
// force_upgrade_below) must raise a global, non-dismissable upgrade overlay —
// not just login. A single handler is registered by the app shell; api.ts fires
// it before throwing so every call site escalates uniformly.

type UpgradeHandler = () => void;
let upgradeHandler: UpgradeHandler | null = null;

/** Register the global forced-upgrade handler (426). One handler max. */
export function onUpgradeRequired(fn: UpgradeHandler | null): void {
  upgradeHandler = fn;
}

// ---- session hint cookie ----
// Non-sensitive boolean marker (NO token material — 00 §4.5 in-memory-only policy
// holds). The refresh cookie is httpOnly so JS cannot see whether a session
// exists; without a hint every anonymous boot would fire a doomed
// POST /v3/auth/refresh and the browser logs a 400 console error.

const HINT = 'ls_auth_hint';

export function hasSessionHint(): boolean {
  return document.cookie.split('; ').some((c) => c === HINT + '=1');
}

function setSessionHint(on: boolean): void {
  document.cookie = on
    ? `${HINT}=1; path=/; max-age=2592000; secure; samesite=strict`
    : `${HINT}=; path=/; max-age=0; secure; samesite=strict`;
}

/**
 * 換票失敗是不是暫時性的（同 iOS RefreshFailure.isTransient / Android RefreshOutcome）：
 * 連不上（fetch 丟 TypeError 等非 ApiError）、5xx、429 → 暫時性，保留登入；
 * 伺服器明確拒絕（4xx）或換票途中 session 已結束（SESSION_ENDED）→ 不是。
 */
export function isTransientRefreshError(e: unknown): boolean {
  if (e instanceof ApiError) {
    if (e.code === 'SESSION_ENDED') return false;
    return e.status >= 500 || e.status === 429;
  }
  return true;
}

// ---- single-flight refresh ----

let refreshing: Promise<string> | null = null;

/**
 * POST /v3/auth/refresh — the ONLY call with credentials:'include' (httpOnly
 * refresh cookie, Path=/v3/auth/refresh). X-Client-Id/Key act as the CSRF gate;
 * no Authorization header and no body. Single-flight: concurrent 401s share one
 * in-flight refresh.
 */
export function refreshAccessToken(): Promise<string> {
  if (refreshing) return refreshing;
  const epoch = authEpoch;
  refreshing = (async () => {
    const res = await fetch(config.apiBase + '/v3/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Id': config.clientId,
        'X-Client-Key': config.clientKey,
        'X-Client-Version': config.clientVersion,
      },
    });
    const body = await res.json().catch(() => ({}) as Record<string, unknown>);
    if (!res.ok) {
      // Refresh cookie gone/invalid — drop the hint so the next boot stays silent.
      if (res.status === 400 || res.status === 401) setSessionHint(false);
      const err = (body as { error?: { code?: string; message?: string } }).error;
      throw new ApiError(res.status, err?.code ?? 'REFRESH_FAILED', err?.message ?? `refresh ${res.status}`, res.headers.get('X-Request-Id'));
    }
    const data = (body as { data?: { access_token?: string; expires_in?: number } }).data;
    const token = data?.access_token;
    if (!token) throw new ApiError(res.status, 'REFRESH_FAILED', 'refresh: no access_token', res.headers.get('X-Request-Id'));
    if (epoch !== authEpoch) {
      // The session ended (logout / hard 401) while this refresh was in flight — do
      // NOT write the rotated token back into a logged-out session.
      throw new ApiError(res.status, 'SESSION_ENDED', 'session ended during refresh', res.headers.get('X-Request-Id'));
    }
    tokenStore.set(token, data?.expires_in);
    setSessionHint(true); // renew the hint's max-age alongside the rotated cookie
    return token;
  })();
  // Release the single-flight slot once settled (swallow here; callers still see the rejection).
  refreshing.then(
    () => {
      refreshing = null;
    },
    () => {
      refreshing = null;
    }
  );
  return refreshing;
}

// ---- core fetch ----

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON-serialized request body. */
  body?: unknown;
  /** 原始內容（例如卡片自訂照片 image/jpeg）；有它就不送 body，Content-Type 用檔案的型別 */
  rawBody?: Blob;
  /** Query params; null/undefined values are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>;
  headers?: Record<string, string>;
  /**
   * Sets the Idempotency-Key header (backend middleware live).
   * The dev Caddy edge allows it in Access-Control-Allow-Headers.
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export interface ApiResult<T> {
  data: T;
  /** X-Request-Id response header (also requires Access-Control-Expose-Headers at the edge). */
  requestId: string | null;
  /** X-Trace-Id response header when exposed by the REST edge. */
  traceId?: string | null;
  /** True when the Idempotent-Replayed response header was present. */
  idempotentReplayed: boolean;
  status: number;
  /**
   * 信封 `meta.next_cursor`（列表端點的翻頁游標，00 §4.4）。沒有 meta / 沒有游標 / 空字串 → null。
   *
   * **不是每個端點都有** —— 只有分頁端點會填。宣告為選填(而不是必填 + null)是因為 `ApiResult`
   * 被大量測試 mock 直接以物件字面量建構;把它變必填會讓那些 mock 全部型別失敗,而它們在意的
   * 是別的東西。呼叫端請用 `!= null`(鬆散),undefined 與 null 都代表「沒有下一頁」。加這一欄是因為在這之前 web 根本讀不到它：
   * 拿了第一頁就當成全部畫出來，而「登入裝置」這一頁在 prod 有 1127 筆、一次只拿得到 100 筆
   * （iOS `APIResult.nextCursor` 早就有這一欄，web 這半邊缺）。
   */
  nextCursor?: string | null;
}

function sameOriginBase(): string {
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
}

export function buildApiUrl(path: string, query?: ApiOptions['query']): string {
  const url = config.apiBase ? new URL(config.apiBase + path) : new URL(path, sameOriginBase());
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== null && v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

/**
 * Calls the v3 REST API. Adds X-Client-Id/Key + Bearer (when a token is held),
 * parses the {data}/{error:{code,message}} envelope, and on a 401 performs ONE
 * transparent refresh-and-replay; a second 401 (or failed refresh) clears the
 * token and fires the onUnauthorized handler.
 */
let lastEdgeDevices: unknown[] = [];
/** 最近一次 GET /v3/devices 看到的 AI 電腦（ls-loop ME）。 */
export function getEdgeDevices<T = { model?: string; online?: { state?: string } | null }>(): T[] {
  return lastEdgeDevices as T[];
}

export async function apiFetch<T = unknown>(path: string, opts: ApiOptions = {}, _retried = false): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {
    'Content-Type': opts.rawBody ? (opts.rawBody.type || 'application/octet-stream') : 'application/json',
    'X-Client-Id': config.clientId,
    'X-Client-Key': config.clientKey,
    'X-Client-Version': config.clientVersion,
    ...opts.headers,
  };
  const token = tokenStore.get();
  if (token) headers['Authorization'] = 'Bearer ' + token;
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  const res = await fetch(buildApiUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.rawBody ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
    signal: opts.signal,
    // No credentials here — the refresh cookie is scoped to /v3/auth/refresh only.
  });

  if (res.status === 401 && !_retried) {
    // 20 §2.1:ACCOUNT_INACTIVE 的 401 是帳號停權的終局訊號,不是 token 過期 —— 不得進
    // refresh 鏈(server 側 refresh 亦被 ADR-024 status re-check 拒絕)。body 解析不出 →
    // fail-safe 維持 refresh 路徑。
    let peek: { error?: { code?: string; message?: string } } | null = null;
    try {
      peek = (await res.clone().json()) as { error?: { code?: string; message?: string } } | null;
    } catch {
      peek = null; // body 解析不出(或環境無 clone)→ fail-safe 走 refresh
    }
    if (peek?.error?.code === 'ACCOUNT_INACTIVE') {
      endAuthSession();
      unauthorizedHandler?.();
      throw new ApiError(401, 'ACCOUNT_INACTIVE', i18n.t('account.errAccountInactive'), res.headers.get('X-Request-Id'));
    }
    try {
      await refreshAccessToken();
    } catch (e) {
      // 暫時性（斷網 / 5xx / 429）不登出：保留登入，這次請求照常失敗（John 2026-10-14；同 iOS / Android）
      if (isTransientRefreshError(e)) {
        throw new ApiError(503, 'NETWORK_UNAVAILABLE', i18n.t('errors.networkUnavailable'), res.headers.get('X-Request-Id'));
      }
      endAuthSession();
      unauthorizedHandler?.();
      throw new ApiError(401, 'UNAUTHORIZED', i18n.t('errors.sessionExpired'), res.headers.get('X-Request-Id'));
    }
    return apiFetch<T>(path, opts, true);
  }

  const requestId = res.headers.get('X-Request-Id');
  const traceId = res.headers.get('X-Trace-Id');
  recordApiExchange({ path, method: opts.method ?? 'GET', status: res.status, requestId, traceId });
  const body = (await res.json().catch(() => ({}))) as {
    data?: T;
    request_id?: string;
    // meta 的形狀各端點不一致（有的沒有、有的不是物件）→ 全部走 optional chaining，
    // 絕不讓一個加值欄位有能力把整個回應變成失敗。
    meta?: { next_cursor?: string | null } | null;
    error?: { code?: string; message?: string; [k: string]: unknown };
  };

  // 426 UPGRADE_REQUIRED on ANY endpoint (00 §4.2 / 20 §8.2): escalate to the
  // global forced-upgrade overlay, then still throw so the caller's flow aborts.
  if (res.status === 426 || body.error?.code === 'UPGRADE_REQUIRED') {
    upgradeHandler?.();
    throw new ApiError(426, body.error?.code ?? 'UPGRADE_REQUIRED', body.error?.message ?? i18n.t('errors.upgradeRequired'), requestId);
  }

  if (res.status === 401) {
    endAuthSession();
    unauthorizedHandler?.();
    // 保留 body 的 domain code(如 ACCOUNT_INACTIVE),讓畫面層的 code 分支能正確顯示原因;
    // 泛用路徑文案不變。
    const code = body.error?.code ?? 'UNAUTHORIZED';
    throw new ApiError(
      401,
      code,
      code === 'ACCOUNT_INACTIVE' ? i18n.t('account.errAccountInactive') : i18n.t('errors.sessionExpired'),
      requestId,
    );
  }
  if (!res.ok) {
    throw new ApiError(res.status, body.error?.code ?? `HTTP_${res.status}`, body.error?.message ?? `HTTP ${res.status}`, requestId, body.error ?? null);
  }

  let data = body.data as T;
  // ls-loop ME：AI 電腦（edge-ai）不是攝影機 → 裝置清單一律不列；另外記下來給事件頁的「AI 電腦離線」提示（不多打一次 API）
  if ((opts.method ?? 'GET') === 'GET' && /^\/v3\/devices\/[^/]+$/.test(path) && body.data && !Array.isArray(body.data)) {
    rememberDeviceTz(body.data as { device_id?: string; tz?: string }); // 攝影機時區（camTz）
  }
  if ((opts.method ?? 'GET') === 'GET' && path === '/v3/devices' && Array.isArray(body.data)) {
    const all = body.data as { model?: string | null; device_id?: string; tz?: string }[];
    all.forEach(rememberDeviceTz); // 攝影機時區（camTz）
    lastEdgeDevices = all.filter(isEdgeAi);
    data = all.filter((d) => !isEdgeAi(d)) as T;
  }
  return {
    data,
    requestId: requestId ?? body.request_id ?? null,
    traceId,
    idempotentReplayed: res.headers.get('Idempotent-Replayed') !== null,
    status: res.status,
    nextCursor:
      typeof body.meta?.next_cursor === 'string' && body.meta.next_cursor !== '' ? body.meta.next_cursor : null,
  };
}

// ---- auth endpoints ----

export interface LoginUser {
  user_id?: string;
  email?: string;
  region?: string;
  role?: string;
  [k: string]: unknown;
}

export interface LoginData {
  access_token: string;
  expires_in: number;
  client_capabilities?: Record<string, unknown>;
  user?: LoginUser;
}

/**
 * POST /v3/auth/login. credentials:'include' so the browser STORES the httpOnly refresh cookie.
 *
 * `device_label`（02 §1.5）以選填欄位帶上，讓「登入裝置」清單認得出這是哪一台。⚠️ 伺服端的
 * login body 是 strict decode（`DisallowUnknownFields`），restful-api!558 **部署之前**送這個
 * 欄位一律 400 —— 所以這裡帶了一條退路：收到 400 INVALID_REQUEST 就退掉標籤重送一次。
 *
 * 為什麼用退路而不是預設關閉的旗標：同一份 code 要能同時對新舊伺服端運作，且 web 與兩個
 * 行動端共用同一套語意（行動端上架後收不回來，更需要這條退路）。只退一次、只對這個碼退：
 * `httpx.DecodeJSON` 在 handler 第一行，這個碼只可能來自 decode 失敗或送出前的必填檢查，
 * 兩者都在任何憑證被消費之前，所以重送是安全的；若 400 另有原因，第二次會拿到同一個錯誤
 * 並照常拋出，代價僅是一次多餘的請求。
 */
export async function login(email: string, password: string): Promise<LoginData> {
  const send = async (deviceLabel: string | null) => {
    const res = await fetch(config.apiBase + '/v3/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Id': config.clientId,
        'X-Client-Key': config.clientKey,
        'X-Client-Version': config.clientVersion,
      },
      // deviceLabel 為 null 時整個 key 不出現(不是送 null)——舊伺服端看的是 key 在不在。
      body: JSON.stringify(deviceLabel === null ? { email, password } : { email, password, device_label: deviceLabel }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      data?: LoginData;
      error?: { code?: string; message?: string };
    };
    if (!res.ok || !body.data?.access_token) {
      throw new ApiError(res.status, body.error?.code ?? `HTTP_${res.status}`, body.error?.message ?? `HTTP ${res.status}`, res.headers.get('X-Request-Id'));
    }
    return body.data;
  };

  const label = detectDeviceLabel();
  let data: LoginData;
  if (label === null) {
    data = await send(null);
  } else {
    try {
      data = await send(label);
    } catch (e) {
      if (e instanceof ApiError && e.status === 400 && e.code === 'INVALID_REQUEST') {
        data = await send(null);
      } else {
        throw e;
      }
    }
  }
  tokenStore.set(data.access_token, data.expires_in);
  setSessionHint(true);
  return data;
}

/** POST /v3/auth/logout — clears the refresh cookie server-side, then drops the in-memory token. */
export async function logout(): Promise<void> {
  try {
    await fetch(config.apiBase + '/v3/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Id': config.clientId,
        'X-Client-Key': config.clientKey,
        'X-Client-Version': config.clientVersion,
        ...(tokenStore.get() ? { Authorization: 'Bearer ' + tokenStore.get() } : {}),
      },
    });
  } catch {
    // network failure on logout is non-fatal; the in-memory token dies regardless
  } finally {
    endAuthSession();
    setSessionHint(false);
  }
}

/** POST /v3/auth/resend-verify — for 412 EMAIL_NOT_VERIFIED on login. */
export async function resendVerify(email: string, region: string = config.region): Promise<void> {
  await apiFetch('/v3/auth/resend-verify', { method: 'POST', body: { email, region } });
}

// ---- registration / password-recovery (pre-auth, 00 §2/§3 / core §2-3, restful §5.1-5.3) ----

export interface RegisterInput {
  email: string;
  password: string;
  display_name: string;
  region?: string;
}

/**
 * 00 §2.3 register response — NO token is issued (account starts
 * status=pending_verification; a verify-email magic link is sent).
 */
export interface RegisterData {
  user_id: string;
  status: string;
  verify_state?: { email?: { sent?: boolean; expires_in?: number } };
}

/** POST /v3/auth/register — pre-auth (X-Client-Id/Key gate). Sends a verify-email link; no session. */
export async function register(input: RegisterInput): Promise<RegisterData> {
  const res = await apiFetch<RegisterData>('/v3/auth/register', {
    method: 'POST',
    body: { ...input, region: input.region ?? config.region },
  });
  return res.data;
}

/** POST /v3/auth/forgot-password — always 200 (anti-enumeration); body never reveals existence. */
export async function forgotPassword(email: string, region: string = config.region): Promise<void> {
  await apiFetch('/v3/auth/forgot-password', { method: 'POST', body: { email, region } });
}

/** POST /v3/auth/reset-password — opaque token from the email link + new password. Revokes all sessions. */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await apiFetch('/v3/auth/reset-password', { method: 'POST', body: { token, new_password: newPassword } });
}

/**
 * POST /v3/auth/verify-email — the email magic-link landing page re-issues the token
 * to the backend so the SPA can show a friendly result. The endpoint is POST-only
 * (consumes the one-shot token); a GET returns 405. 410 VERIFY_TOKEN_EXPIRED on
 * expired/used tokens.
 */
export async function verifyEmail(token: string): Promise<void> {
  await apiFetch('/v3/auth/verify-email', { method: 'POST', body: { token } });
}

// ---- device per-user fields (spec 21 §4；PATCH /v3/devices/{id}；CAM-24) ----

/**
 * PATCH /v3/devices/{id} group — per-user 分組（device_user_relations.group）。
 * 任何 active 成員可設自己那列；空字串 = 清除分組（未分組），max 32。與 aliasname 同端點、同語意，
 * 只有呼叫者自己看得到。
 */
export async function setDeviceGroup(deviceId: string, group: string): Promise<void> {
  await apiFetch(`/v3/devices/${encodeURIComponent(deviceId)}`, { method: 'PATCH', body: { group } });
}

// ---- user preferences blob (GET/PUT /v3/me/preferences；CAM-24) ----

/** 使用者偏好 blob。目前僅承載裝置分組排序（device_group_order）；未知鍵原樣保留（見 setDeviceGroupOrder）。 */
export interface UserPreferences {
  /** 裝置分組顯示順序（群組名字串陣列；CAM-24）。缺/空＝未排序。 */
  device_group_order?: string[];
  [k: string]: unknown;
}

/** GET /v3/me/preferences → 偏好 blob（缺 → 空物件）。 */
export async function getPreferences(): Promise<UserPreferences> {
  const res = await apiFetch<UserPreferences>('/v3/me/preferences');
  return res.data ?? {};
}

/** PUT /v3/me/preferences → 整筆取代偏好 blob。 */
export async function putPreferences(prefs: UserPreferences): Promise<void> {
  await apiFetch('/v3/me/preferences', { method: 'PUT', body: prefs });
}

/** 寫單一偏好鍵：read-merge-write 保留其他鍵（外觀 theme 等）。 */
export async function setPreferenceKey(key: string, value: unknown): Promise<void> {
  const prefs = await getPreferences();
  await putPreferences({ ...prefs, [key]: value });
}

/** 讀裝置分組排序（device_group_order；缺/非陣列 → 空陣列）。 */
export async function getDeviceGroupOrder(): Promise<string[]> {
  const prefs = await getPreferences();
  return Array.isArray(prefs.device_group_order) ? prefs.device_group_order : [];
}

/** 寫裝置分組排序：read-merge-write 保留其他偏好鍵，避免整筆 PUT 覆蓋掉未知鍵。 */
export async function setDeviceGroupOrder(order: string[]): Promise<void> {
  const prefs = await getPreferences();
  await putPreferences({ ...prefs, device_group_order: order });
}

// ---- push tokens (20 §8.1, restful 00 §5.7) ----

/** DELETE /v3/me/push-tokens/{token} — turning the notification master switch off. */
export async function deletePushToken(token: string): Promise<void> {
  await apiFetch(`/v3/me/push-tokens/${encodeURIComponent(token)}`, { method: 'DELETE' });
}

// ---- cloud-plan memberships (account-scoped subscriptions; 00 §9.1) ----
// Distinct from the per-device SubscriptionView: /v3/subscriptions is USER-scoped and
// returns the memberships the signed-in user owns, each carrying its own device roster
// ceiling (max_devices) and current usage (device_count). start_at/expire_at are int64
// MICROSECONDS (same unit as formatTimestampUs consumes). Billing mutations (attach)
// carry an Idempotency-Key per the billing contract; detach does not.

/** GET /v3/subscriptions item — one account-level cloud-plan membership. */
export interface UserSubscriptionView {
  subscription_id: string;
  user_id: string;
  plan_id: string;
  plan_version: number;
  status: string; // active | trialing | past_due | grace | cancelled | expired …
  start_at: number; // int64 microseconds → formatTimestampUs
  expire_at: number; // int64 microseconds → formatTimestampUs
  max_devices: number;
  device_count: number;
  /**
   * D32-R1 §8.1 lifecycle fields. Lifecycle is owned by the provider that SOLD the
   * subscription; the client only routes to that provider's manage surface.
   * billing_provider: which provider to route to — 'apple'|'google' manage in-store,
   * 'stripe' manages via the Billing Portal (POST …:portal); '' = legacy/unknown.
   * (external_subscription_id / stripe_customer_id are server-internal — never in this view.)
   */
  billing_provider?: 'apple' | 'google' | 'stripe' | '';
  /** Renewal armed. cancel_pending=false + auto_renew=true is the steady active state. */
  auto_renew?: boolean;
  /**
   * TRUE = user turned off renewal / cancelled. Subscription is STILL ACTIVE and
   * entitled until expire_at; the expiry worker flips it to expired at period end.
   * Display "已取消 · 服務至 {expire_at}" when true.
   */
  cancel_pending?: boolean;
  /**
   * Enriched device roster — the device_ids attached to this membership. Present on
   * the attach response (POST .../devices) and, when the list endpoint enriches, on
   * GET /v3/subscriptions. Optional because older payloads carry only device_count;
   * callers §3.7 use `(devices ?? [])` to test membership before attaching (D19-a).
   */
  devices?: string[];
  /**
   * restful !583 — expiry-lifecycle enrichment. Server-owned, collapsed to two states.
   * All optional (JSON omitempty on the server): boundary / legacy payloads may omit
   * them, so the client MUST fall back (missing display_status → treat as active) rather
   * than re-deriving from `status`/`expire_at` (that clock is the server's job).
   *
   * display_status: 'active' | 'expired' — the ONLY signal the UI uses to render the
   *   status label. Note cancel_pending===true is still ACTIVE (service until expire_at).
   */
  display_status?: 'active' | 'expired';
  /** can_renew: TRUE only for a PAID plan that has expired → gates the Renew button. Free/active → false. */
  can_renew?: boolean;
  /** can_delete: TRUE once expired → gates the Delete (soft-hide) button. */
  can_delete?: boolean;
  /**
   * renewal_token: opaque stable-original identity the IAP (apple/google) renew flow echoes
   * back so the server extends the SAME subscription instead of creating a new one. Server
   * fills it ONLY for a can_renew IAP sub scoped to the owner; stripe/free/active subs omit it.
   * Web never renews via IAP (it re-buys on the Stripe Checkout rail — server-side reactivation
   * by stripe sub/customer id), so this field is carried purely for DTO type parity and is
   * unused by the web client. Opaque: never parsed, only round-tripped.
   */
  renewal_token?: string;
  /**
   * hidden: mirrors the master soft-hide flag. GET /v3/subscriptions filters hidden===true
   * server-side, so a listed view is ~always false; the dismiss response carries hidden=true.
   */
  hidden?: boolean;
}

/** GET /v3/subscriptions — the signed-in user's cloud-plan memberships. */
export async function listSubscriptions(): Promise<UserSubscriptionView[]> {
  const res = await apiFetch<UserSubscriptionView[]>('/v3/subscriptions');
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * POST /v3/subscriptions/{id}/devices — attach a device to a membership. A fresh
 * Idempotency-Key per attempt (a retry after failure MUST use a new key). Returns the
 * updated view (device_count incremented) so the caller can replace it in state.
 */
export async function addSubscriptionDevice(subscriptionId: string, deviceId: string): Promise<UserSubscriptionView> {
  const res = await apiFetch<UserSubscriptionView>(`/v3/subscriptions/${encodeURIComponent(subscriptionId)}/devices`, {
    method: 'POST',
    body: { device_id: deviceId },
    idempotencyKey: crypto.randomUUID(),
  });
  return res.data;
}

/** DELETE /v3/subscriptions/{id}/devices/{device_id} — detach a device. Returns the updated view. */
export async function removeSubscriptionDevice(subscriptionId: string, deviceId: string): Promise<UserSubscriptionView> {
  const res = await apiFetch<UserSubscriptionView>(
    `/v3/subscriptions/${encodeURIComponent(subscriptionId)}/devices/${encodeURIComponent(deviceId)}`,
    { method: 'DELETE' },
  );
  return res.data;
}

/**
 * POST /v3/subscriptions/{id}/dismiss — soft-hide an EXPIRED membership (restful !583).
 * owner-scoped scopedMut → carries a fresh Idempotency-Key per attempt (a retry MUST use a
 * new key), same discipline as attach/detach. Returns the updated view (hidden=true) so the
 * caller can reconcile in one round-trip; a raced hard-delete degrades to {subscription_id}.
 * 409 CANNOT_DISMISS_ACTIVE when the sub is still active (should not happen — the button only
 * shows when can_delete===true). After success the caller drops the sub from state (the next
 * List no longer returns it, since the server filters hidden).
 */
export async function dismissSubscription(subscriptionId: string): Promise<UserSubscriptionView> {
  const res = await apiFetch<UserSubscriptionView>(
    `/v3/subscriptions/${encodeURIComponent(subscriptionId)}/dismiss`,
    { method: 'POST', idempotencyKey: crypto.randomUUID() },
  );
  return res.data;
}

// ---- effective entitlements (spec 00 §... / 02; GET /v3/devices/{id}/entitlements) ----
// The plan-resolved entitlements for a device. `recording_modes`/`locations` gate the
// recording pickers (which record MODES the plan allows, whether cloud is a permitted
// location). Shared shape used by the account subscription page and the device settings
// recording section (parity with the native cloudRecordingAllowed helper).

/** Plan entitlements block (00 §9.1 plan_entitlements / effective entitlements). */
export interface PlanEntitlements {
  recording_enabled?: boolean;
  recording_modes?: string[];
  retention_days?: number;
  quality_max?: string;
  locations?: string[];
  storage_gb?: number;
  relay_gb?: number;
  share_users?: number;
  ai_features?: string[];
  ai_monthly_units?: number;
  firmware_channels?: string[];
}

/** One source layer in the entitlement decision chain (explainer). */
export interface ExplainStage {
  layer: string;
  status: string;
  code?: string;
  details?: Record<string, unknown>;
}

/** GET /v3/devices/{id}/entitlements — effective entitlements + decision/complete. */
export interface EffectiveEntitlementExplain {
  decision: 'effective' | 'provisional' | 'denied';
  complete: boolean;
  entitlements: PlanEntitlements;
  stages: ExplainStage[];
  snapshot_version: string;
}

/**
 * GET /v3/devices/{id}/entitlements — the device's effective entitlements + the
 * decision/complete envelope (spec 00/02). Used to gate the recording MODE picker
 * (entitlements.recording_modes) and the cloud LOCATION option (entitlements.locations).
 */
export async function getDeviceEntitlements(deviceId: string): Promise<EffectiveEntitlementExplain> {
  const res = await apiFetch<EffectiveEntitlementExplain>(`/v3/devices/${encodeURIComponent(deviceId)}/entitlements`);
  return res.data;
}

/**
 * DEV-ONLY: POST /v3/subscriptions/{id}/cancel — owner self-serve cancel/remove of a membership,
 * for testing. The backend route is gated by RA_ALLOW_DEV_SUBSCRIPTION_CANCEL (forced off + not
 * registered in prod, where cancellation is provider-owned). mode="period_end" sets cancel_pending
 * (still active until expire_at); mode="immediate" hard-expires + tears down (clean slate). Returns
 * the updated view.
 */
export async function cancelSubscriptionDev(subscriptionId: string, mode: 'period_end' | 'immediate'): Promise<UserSubscriptionView> {
  const res = await apiFetch<UserSubscriptionView>(
    `/v3/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`,
    { method: 'POST', body: { mode } },
  );
  return res.data;
}

/**
 * POST /v3/subscriptions:checkout — start a web Stripe Checkout for a cloud plan.
 * Returns the Stripe-hosted checkout URL; the caller redirects the browser there.
 * (Web purchase rail per D32-R1; native apps use in-app IAP instead.)
 */
export async function startSubscriptionCheckout(planId: string, planVersion?: number): Promise<string> {
  const res = await apiFetch<{ checkout_url: string }>('/v3/subscriptions:checkout', {
    method: 'POST',
    body: { plan_id: planId, ...(planVersion != null ? { plan_version: planVersion } : {}) },
  });
  return res.data.checkout_url;
}

// ---- one-time free plan (00 §9.1) ----
// "Free is a plan" (default_free=true). Claimable ONCE per account: after claiming
// (even once expired/removed) the account is no longer eligible. Distinct rail from
// the paid purchase (Stripe checkout / native IAP) — :claim-free carries NO body and
// NO Idempotency-Key.

/** GET /v3/subscriptions:free-eligibility — whether this account may still claim the free plan. */
export interface FreeEligibility {
  eligible: boolean;
  plan_id: string;
}

/**
 * POST /v3/subscriptions:claim-free — claim the one-time free plan. NO body, NO
 * Idempotency-Key. Returns the created membership (same shape as a paid create).
 * 409 FREE_ALREADY_USED once the account has already claimed it; 404 NO_FREE_PLAN
 * when no published default_free plan exists — both surface as an ApiError.
 */
export async function claimFreeSubscription(): Promise<UserSubscriptionView> {
  const res = await apiFetch<UserSubscriptionView>('/v3/subscriptions:claim-free', { method: 'POST' });
  return res.data;
}

/** GET /v3/subscriptions:free-eligibility — best-effort at the call site (failure → treat as not eligible). */
export async function freeEligibility(): Promise<FreeEligibility> {
  const res = await apiFetch<FreeEligibility>('/v3/subscriptions:free-eligibility');
  return res.data;
}

/**
 * POST /v3/subscriptions/{id}:portal — open a Stripe Billing Portal session for a
 * membership the user bought on the web (billing_provider==='stripe'). The portal
 * hosts cancel / update-payment / change-plan; the caller redirects the browser to
 * the returned URL. Server returns 409 NOT_STRIPE_MANAGED for apple/google memberships
 * (those manage in-store, not here) — surfaced to the caller as an ApiError.
 * (Manage rail per D32-R1 §8.1; owner-authz, scope subscription:manage.)
 */
export async function startSubscriptionPortal(subscriptionId: string): Promise<{ portal_url: string }> {
  const res = await apiFetch<{ portal_url: string }>(
    `/v3/subscriptions/${encodeURIComponent(subscriptionId)}:portal`,
    { method: 'POST' },
  );
  return { portal_url: res.data.portal_url };
}
